/**
 * 自動定位觸發元件
 * 在用戶登入後自動觸發定位，並處理防重複觸發機制
 */

import React, { useEffect, useRef } from 'react';
import { useContext } from 'react';
import AuthContext from '../context/authContext';
import { LocationData } from '../types/CustomType';
import { devLog } from '../utils/devLogger';

interface AutoLocationTriggerProps {
  /**
   * 定位完成回調函數
   */
  onLocationUpdate?: (location: LocationData) => void;

  /**
   * 是否啟用自動定位（預設為 true）
   */
  enabled?: boolean;

  /**
   * 延遲觸發時間（毫秒，預設 1500ms）
   */
  delay?: number;
}

/**
 * 自動定位觸發元件
 *
 * 這個元件會在以下情況自動觸發定位：
 * 1. 用戶已登入
 * 2. 當前會話中沒有位置資訊
 * 3. enabled 為 true
 *
 * @param props AutoLocationTriggerProps
 * @returns React 元件（不渲染任何內容）
 */
export const AutoLocationTrigger: React.FC<AutoLocationTriggerProps> = ({
  onLocationUpdate,
  enabled = true,
  delay = 1500
}) => {
  const {
    isMobileLogin,
    currentLocation,
    isLocating,
    triggerAutoLocation,
    checkLocationNeeded
  } = useContext(AuthContext);

  // 使用 ref 來追蹤是否已經觸發過定位
  const hasTriggeredRef = useRef<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // 清理函數
    const cleanup = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    // 檢查是否需要觸發自動定位
    const shouldTrigger = () => {
      // 基本條件檢查
      if (!enabled) {
        devLog('🚫 AutoLocationTrigger: 已停用');
        return false;
      }

      if (!isMobileLogin) {
        devLog('🚫 AutoLocationTrigger: 用戶未登入');
        return false;
      }

      if (isLocating) {
        devLog('🚫 AutoLocationTrigger: 正在定位中');
        return false;
      }

      if (hasTriggeredRef.current) {
        devLog('🚫 AutoLocationTrigger: 已觸發過定位');
        return false;
      }

      if (!checkLocationNeeded()) {
        devLog('🚫 AutoLocationTrigger: 不需要定位（已有位置資訊）');
        return false;
      }

      return true;
    };

    // 執行自動定位
    const executeAutoLocation = async () => {
      try {
        devLog('🎯 AutoLocationTrigger: 開始執行自動定位...');
        hasTriggeredRef.current = true;

        const location = await triggerAutoLocation();

        devLog('✅ AutoLocationTrigger: 自動定位完成', {
          來源: location.source,
          座標: `${location.lat}, ${location.lng}`,
          精度: location.accuracy ? `${location.accuracy}m` : '未知'
        });

        // 回調通知
        if (onLocationUpdate) {
          onLocationUpdate(location);
        }

      } catch (error) {
        console.error('❌ AutoLocationTrigger: 自動定位發生錯誤', error);
        hasTriggeredRef.current = false; // 重置以允許重試
      }
    };

    // 主邏輯
    if (shouldTrigger()) {
      devLog(`⏰ AutoLocationTrigger: ${delay}ms 後觸發自動定位...`);

      timeoutRef.current = setTimeout(() => {
        executeAutoLocation();
      }, delay);
    }

    // 清理函數
    return cleanup;

  }, [
    enabled,
    isMobileLogin,
    currentLocation,
    isLocating,
    checkLocationNeeded,
    triggerAutoLocation,
    onLocationUpdate,
    delay
  ]);

  // 重置觸發標記當登入狀態改變時
  useEffect(() => {
    if (!isMobileLogin) {
      devLog('🔄 AutoLocationTrigger: 用戶登出，重置觸發標記');
      hasTriggeredRef.current = false;
    }
  }, [isMobileLogin]);

  // 元件卸載時清理
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        devLog('🧹 AutoLocationTrigger: 元件卸載，清理定時器');
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // 此元件不渲染任何視覺內容
  return null;
};

/**
 * 簡化版自動定位觸發器
 * 僅在用戶登入且沒有位置資訊時觸發
 */
export const SimpleAutoLocationTrigger: React.FC<{
  onLocationUpdate?: (location: LocationData) => void;
}> = ({ onLocationUpdate }) => {
  return (
    <AutoLocationTrigger
      onLocationUpdate={onLocationUpdate}
      enabled={true}
      delay={1000}
    />
  );
};

export default AutoLocationTrigger;
